class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification =
      this.querySelector('#cart-notification') ||
      document.getElementById('cart-notification');

    if (!this.notification) return;

    this.header = document.querySelector('sticky-header');
    this.activeElement = null;
    this.currentCart = null;
    this.currentItem = null;
    this.scrollPosition = 0;

    this.hostSection =
      this.closest('.shopify-section') ||
      this.notification.closest('.shopify-section');

    this.mobileMedia = window.matchMedia(
      '(max-width: 749px)'
    );

    this.viewportListenerAttached = false;

    this.onBodyClick =
      this.handleBodyClick.bind(this);

    this.onOverlayClick =
      this.close.bind(this);

    this.onNotificationClick =
      this.handleNotificationClick.bind(this);

    this.onViewportChange =
      this.handleViewportChange.bind(this);

    this.notification.addEventListener(
      'keyup',
      (event) => {
        if (event.code === 'Escape') {
          this.close();
        }
      }
    );

    this.notification.addEventListener(
      'click',
      this.onNotificationClick
    );

    this.ensureOverlay();
  }

  connectedCallback() {
    this.attachViewportListener();
  }

  disconnectedCallback() {
    this.detachViewportListener();

    document.body.removeEventListener(
      'click',
      this.onBodyClick
    );

    this.overlay?.removeEventListener(
      'click',
      this.onOverlayClick
    );
  }

  attachViewportListener() {
    if (
      !this.mobileMedia ||
      this.viewportListenerAttached
    ) {
      return;
    }

    if (this.mobileMedia.addEventListener) {
      this.mobileMedia.addEventListener(
        'change',
        this.onViewportChange
      );
    } else {
      this.mobileMedia.addListener(
        this.onViewportChange
      );
    }

    this.viewportListenerAttached = true;
  }

  detachViewportListener() {
    if (
      !this.mobileMedia ||
      !this.viewportListenerAttached
    ) {
      return;
    }

    if (this.mobileMedia.removeEventListener) {
      this.mobileMedia.removeEventListener(
        'change',
        this.onViewportChange
      );
    } else {
      this.mobileMedia.removeListener(
        this.onViewportChange
      );
    }

    this.viewportListenerAttached = false;
  }

  get isMobile() {
    return this.mobileMedia.matches;
  }

  get language() {
    const language =
      document.documentElement.lang ||
      window.Shopify?.locale ||
      'de';

    return language
      .toLowerCase()
      .startsWith('de')
      ? 'de'
      : 'en';
  }

  get strings() {
    if (this.language === 'de') {
      return {
        subtitle:
          'Ihr ausgewähltes Produkt wartet bereits im Warenkorb.',
        productPrice: 'Produktpreis',
        subtotal: 'Zwischensumme',
        checkout: 'Auschecken',
        checkoutSubline: 'Sicher & schnell bezahlen',
        viewCart: 'Warenkorb ansehen',
        continueShopping: 'Weiter shoppen',
        paymentLabel: 'Sicher bezahlen mit',
        tracking: 'Lieferung mit Tracking',
        returns: '30 Tage Rückgabe',
        securePayment: 'Sichere Zahlung',
        loading: 'Warenkorb wird geladen …',
        itemSingular: 'Artikel',
        itemPlural: 'Artikel',
        inCart: 'im Warenkorb',
        itemCountLabel: 'Artikel im Warenkorb',
      };
    }

    return {
      subtitle:
        'Your selected product is already waiting in your cart.',
      productPrice: 'Product price',
      subtotal: 'Subtotal',
      checkout: 'Checkout',
      checkoutSubline: 'Secure & fast payment',
      viewCart: 'View cart',
      continueShopping: 'Continue shopping',
      paymentLabel: 'Secure payment with',
      tracking: 'Shipping with tracking',
      returns: '30-day returns',
      securePayment: 'Secure payment',
      loading: 'Loading cart …',
      itemSingular: 'item',
      itemPlural: 'items',
      inCart: 'in cart',
      itemCountLabel: 'items in cart',
    };
  }

  ensureOverlay() {
    let overlay = document.querySelector(
      '.hlp-cart-notification-overlay'
    );

    if (!overlay) {
      overlay = document.createElement('div');

      overlay.className =
        'hlp-cart-notification-overlay';

      overlay.setAttribute(
        'aria-hidden',
        'true'
      );

      document.body.appendChild(overlay);
    }

    this.overlay = overlay;

    this.overlay.removeEventListener(
      'click',
      this.onOverlayClick
    );

    this.overlay.addEventListener(
      'click',
      this.onOverlayClick
    );
  }

  open() {
    if (!this.notification) return;

    this.notification.classList.add(
      'animate'
    );

    if (this.isMobile) {
      this.activateMobileMode();
    } else {
      this.deactivateMobileMode();
    }

    requestAnimationFrame(() => {
      this.notification.classList.add(
        'active'
      );
    });

    const focusNotification = () => {
      if (
        !this.notification.classList.contains(
          'active'
        )
      ) {
        return;
      }

      this.notification.focus();

      if (
        typeof trapFocus === 'function'
      ) {
        trapFocus(this.notification);
      }
    };

    const onTransitionEnd = (event) => {
      if (
        event.target !== this.notification
      ) {
        return;
      }

      this.notification.removeEventListener(
        'transitionend',
        onTransitionEnd
      );

      focusNotification();
    };

    this.notification.addEventListener(
      'transitionend',
      onTransitionEnd
    );

    window.setTimeout(
      focusNotification,
      450
    );

    document.body.addEventListener(
      'click',
      this.onBodyClick
    );

    this.dispatchCartViewEvent();
  }

  close() {
    if (!this.notification) return;

    this.notification.classList.remove(
      'active'
    );

    this.deactivateMobileMode();

    document.body.removeEventListener(
      'click',
      this.onBodyClick
    );

    if (
      typeof removeTrapFocus === 'function'
    ) {
      removeTrapFocus(
        this.activeElement
      );
    }
  }

  activateMobileMode() {
    this.lockPage();

    this.overlay?.classList.add(
      'active'
    );

    this.overlay?.setAttribute(
      'aria-hidden',
      'false'
    );

    this.hostSection?.classList.add(
      'hlp-cart-host-active'
    );

    document.documentElement.classList.add(
      'hlp-cart-notification-open'
    );
  }

  deactivateMobileMode() {
    this.overlay?.classList.remove(
      'active'
    );

    this.overlay?.setAttribute(
      'aria-hidden',
      'true'
    );

    this.hostSection?.classList.remove(
      'hlp-cart-host-active'
    );

    document.documentElement.classList.remove(
      'hlp-cart-notification-open'
    );

    this.unlockPage();
  }

  lockPage() {
    if (!this.isMobile) return;

    if (
      document.body.classList.contains(
        'hlp-cart-notification-lock'
      )
    ) {
      return;
    }

    this.scrollPosition =
      window.scrollY ||
      document.documentElement.scrollTop ||
      0;

    document.body.style.top =
      `-${this.scrollPosition}px`;

    document.body.classList.add(
      'hlp-cart-notification-lock'
    );
  }

  unlockPage() {
    if (
      !document.body.classList.contains(
        'hlp-cart-notification-lock'
      )
    ) {
      return;
    }

    document.body.classList.remove(
      'hlp-cart-notification-lock'
    );

    document.body.style.top = '';

    window.scrollTo({
      top: this.scrollPosition,
      left: 0,
      behavior: 'auto',
    });
  }

  handleViewportChange(event) {
    /*
     * Re-check the enhanced structure whenever the viewport
     * crosses the mobile breakpoint.
     */
    this.prepareEnhancedLayout();

    const notificationIsOpen =
      this.notification.classList.contains(
        'active'
      );

    if (!notificationIsOpen) {
      this.deactivateMobileMode();
      return;
    }

    if (event.matches) {
      /*
       * Desktop → mobile.
       */
      this.activateMobileMode();
      this.refreshCartData();
    } else {
      /*
       * Mobile → desktop.
       */
      this.deactivateMobileMode();
    }
  }

  renderContents(parsedState) {
    this.cartItemKey =
      parsedState.key;

    this.getSectionsToRender().forEach(
      (section) => {
        const target =
          document.getElementById(
            section.id
          );

        const sectionHTML =
          parsedState.sections?.[
            section.id
          ];

        if (
          !target ||
          !sectionHTML
        ) {
          return;
        }

        const innerHTML =
          this.getSectionInnerHTML(
            sectionHTML,
            section.selector
          );

        if (innerHTML !== null) {
          target.innerHTML =
            innerHTML;
        }
      }
    );

    if (
      this.header &&
      typeof this.header.reveal ===
        'function'
    ) {
      this.header.reveal();
    }

    /*
     * Always build the enhanced structure.
     * CSS decides whether it appears as mobile or desktop.
     */
    this.prepareEnhancedLayout();
    this.refreshCartData();

    this.open();
  }

  prepareEnhancedLayout() {
    if (!this.notification) return;

    const header =
      this.notification.querySelector(
        '.cart-notification__header'
      );

    const productContainer =
      this.notification.querySelector(
        '#cart-notification-product'
      );

    const links =
      this.notification.querySelector(
        '.cart-notification__links'
      );

    if (
      !header ||
      !productContainer ||
      !links
    ) {
      return;
    }

    this.addDragHandle();
    this.addSubtitle(header);

    this.structureProductCard(
      productContainer
    );

    this.addInlineSummary(
      productContainer
    );

    this.addTrustRow(links);
    this.decorateActionButtons(links);
    this.addPaymentMethods(links);
  }

  addDragHandle() {
    let dragHandle =
      this.notification.querySelector(
        '.hlp-cart-drag'
      );

    if (dragHandle) return;

    dragHandle =
      document.createElement('div');

    dragHandle.className =
      'hlp-cart-drag';

    dragHandle.setAttribute(
      'aria-hidden',
      'true'
    );

    this.notification.prepend(
      dragHandle
    );
  }

  addSubtitle(header) {
    let subtitle =
      this.notification.querySelector(
        '.hlp-cart-notification__subtitle'
      );

    if (!subtitle) {
      subtitle =
        document.createElement('p');

      subtitle.className =
        'hlp-cart-notification__subtitle';

      header.insertAdjacentElement(
        'afterend',
        subtitle
      );
    }

    subtitle.textContent =
      this.strings.subtitle;
  }

  structureProductCard(
    productContainer
  ) {
    const existingMain =
      productContainer.querySelector(
        '.hlp-cart-product-main'
      );

    if (existingMain) {
      this.identifyProductCopy(
        existingMain
      );

      return;
    }

    /*
     * Prefer Dawn's existing inner product wrapper.
     */
    let main =
      productContainer.querySelector(
        '.cart-notification-product__inner'
      );

    /*
     * Theme variants sometimes use another direct wrapper.
     */
    if (!main) {
      const directWrappers =
        Array.from(
          productContainer.children
        ).filter((child) => {
          return !child.classList.contains(
            'hlp-cart-inline-summary'
          );
        });

      main = directWrappers.find(
        (child) => {
          return Boolean(
            child.querySelector(
              '.cart-notification-product__image'
            ) &&
            (
              child.querySelector(
                '.cart-notification-product__name'
              ) ||
              child.querySelector(
                '.cart-notification-product__title'
              )
            )
          );
        }
      );
    }

    if (main) {
      main.classList.add(
        'hlp-cart-product-main'
      );

      this.identifyProductCopy(main);
      return;
    }

    /*
     * Safe fallback for themes where the image and text
     * are direct children of the product container.
     */
    const children =
      Array.from(
        productContainer.children
      ).filter((child) => {
        return !child.classList.contains(
          'hlp-cart-inline-summary'
        );
      });

    if (children.length === 0) {
      return;
    }

    const createdMain =
      document.createElement('div');

    const createdCopy =
      document.createElement('div');

    createdMain.className =
      'hlp-cart-product-main hlp-cart-product-main--created';

    createdCopy.className =
      'hlp-cart-product-copy hlp-cart-product-copy--created';

    const imageElement =
      children.find((child) => {
        return (
          child.matches?.(
            '.cart-notification-product__image'
          ) ||
          child.querySelector?.(
            '.cart-notification-product__image'
          )
        );
      });

    if (imageElement) {
      createdMain.appendChild(
        imageElement
      );
    } else {
      createdMain.classList.add(
        'hlp-cart-product-main--no-image'
      );
    }

    children.forEach((child) => {
      if (
        child === imageElement
      ) {
        return;
      }

      createdCopy.appendChild(child);
    });

    createdMain.appendChild(
      createdCopy
    );

    productContainer.prepend(
      createdMain
    );
  }

  identifyProductCopy(main) {
    const directChildren =
      Array.from(main.children);

    const imageChild =
      directChildren.find(
        (child) => {
          return (
            child.matches?.(
              '.cart-notification-product__image'
            ) ||
            child.querySelector?.(
              '.cart-notification-product__image'
            )
          );
        }
      );

    const copyChild =
      directChildren.find(
        (child) => {
          if (
            child === imageChild
          ) {
            return false;
          }

          return Boolean(
            child.matches?.(
              '.cart-notification-product__name, .cart-notification-product__title'
            ) ||
            child.querySelector?.(
              '.cart-notification-product__name, .cart-notification-product__title, .caption-with-letter-spacing, dl'
            )
          );
        }
      );

    if (copyChild) {
      copyChild.classList.add(
        'hlp-cart-product-copy'
      );
    }

    if (!imageChild) {
      main.classList.add(
        'hlp-cart-product-main--no-image'
      );
    }
  }

  addInlineSummary(
    productContainer
  ) {
    let summary =
      productContainer.querySelector(
        '.hlp-cart-inline-summary'
      );

    if (!summary) {
      summary =
        document.createElement('div');

      summary.className =
        'hlp-cart-inline-summary';

      productContainer.appendChild(
        summary
      );
    }

    summary.innerHTML = `
      <div class="hlp-cart-inline-summary__price">
        <span>
          ${this.strings.productPrice}
        </span>

        <strong data-hlp-product-price>
          —
        </strong>
      </div>

      <div class="hlp-cart-inline-summary__cart">
        <span data-hlp-cart-count>
          ${this.strings.loading}
        </span>

        <div class="hlp-cart-inline-summary__total">
          <small>
            ${this.strings.subtotal}
          </small>

          <strong data-hlp-cart-total>
            —
          </strong>
        </div>
      </div>
    `;
  }

  addTrustRow(links) {
    let trust =
      this.notification.querySelector(
        '.hlp-cart-trust'
      );

    if (!trust) {
      trust =
        document.createElement('div');

      trust.className =
        'hlp-cart-trust';

      links.insertAdjacentElement(
        'beforebegin',
        trust
      );
    }

    trust.innerHTML = `
      <div class="hlp-cart-trust__item">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M3 7h11v10H3z"/>
          <path d="M14 10h4l3 3v4h-7"/>
          <path d="M7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/>
          <path d="M18 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/>
        </svg>

        <strong>
          ${this.strings.tracking}
        </strong>
      </div>

      <div class="hlp-cart-trust__item">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9 7 4 12l5 5"/>
          <path d="M4 12h8a7 7 0 0 1 7 7v1"/>
        </svg>

        <strong>
          ${this.strings.returns}
        </strong>
      </div>

      <div class="hlp-cart-trust__item">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect
            x="5"
            y="10"
            width="14"
            height="11"
            rx="2"
          />
          <path d="M8 10V7a4 4 0 0 1 8 0v3"/>
          <path d="M9.5 15.5l1.7 1.7 3.6-3.8"/>
        </svg>

        <strong>
          ${this.strings.securePayment}
        </strong>
      </div>
    `;
  }

  decorateActionButtons(links) {
    const checkoutForm =
      links.querySelector(
        '#cart-notification-form'
      );

    const checkoutButton =
      checkoutForm?.querySelector(
        'button[name="checkout"]'
      );

    const cartButton =
      links.querySelector(
        '#cart-notification-button'
      );

    const continueButton =
      Array.from(
        links.querySelectorAll(
          'button[type="button"]'
        )
      ).find((button) => {
        return !button.classList.contains(
          'cart-notification__close'
        );
      });

    if (checkoutForm) {
      links.prepend(
        checkoutForm
      );
    }

    if (
      cartButton &&
      checkoutForm
    ) {
      checkoutForm.insertAdjacentElement(
        'afterend',
        cartButton
      );
    }

    if (continueButton) {
      links.appendChild(
        continueButton
      );
    }

    if (checkoutButton) {
      checkoutButton.classList.add(
        'hlp-cart-action',
        'hlp-cart-action--primary'
      );

      checkoutButton.innerHTML = `
        <span class="hlp-cart-action__content">
          <strong>
            ${this.strings.checkout}
          </strong>

          <small>
            ${this.strings.checkoutSubline}
          </small>
        </span>

        <span
          class="hlp-cart-action__arrow"
          aria-hidden="true"
        >
          ›
        </span>
      `;
    }

    if (cartButton) {
      cartButton.classList.add(
        'hlp-cart-action',
        'hlp-cart-action--secondary'
      );

      cartButton.innerHTML = `
        <span data-hlp-cart-button-text>
          ${this.strings.viewCart}
        </span>

        <span
          class="hlp-cart-action__arrow"
          aria-hidden="true"
        >
          ›
        </span>
      `;
    }

    if (continueButton) {
      continueButton.classList.add(
        'hlp-cart-continue'
      );

      continueButton.textContent =
        this.strings.continueShopping;
    }
  }

  addPaymentMethods(links) {
    let payment =
      this.notification.querySelector(
        '.hlp-cart-payment'
      );

    if (!payment) {
      payment =
        document.createElement('div');

      payment.className =
        'hlp-cart-payment';

      links.insertAdjacentElement(
        'afterend',
        payment
      );
    }

    payment.innerHTML = `
      <p class="hlp-cart-payment__label">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect
            x="5"
            y="10"
            width="14"
            height="11"
            rx="2"
          />
          <path d="M8 10V7a4 4 0 0 1 8 0v3"/>
        </svg>

        <span>
          ${this.strings.paymentLabel}
        </span>
      </p>

      <div class="hlp-cart-payment__icons"></div>
    `;

    const iconHolder =
      payment.querySelector(
        '.hlp-cart-payment__icons'
      );

    const possibleSources =
      Array.from(
        document.querySelectorAll(
          '.hlp-payment-icons, .list-payment, .payment-icons'
        )
      );

    const source =
      possibleSources.find(
        (element) => {
          return !this.notification.contains(
            element
          );
        }
      );

    if (source) {
      this.clonePreferredPaymentMethods(
        source,
        iconHolder
      );

      return;
    }

    this.addFallbackPaymentMethods(
      iconHolder
    );
  }

  clonePreferredPaymentMethods(
    source,
    iconHolder
  ) {
    const candidates =
      Array.from(source.children);

    const preferredGroups = [
      ['american express', 'amex'],
      ['apple pay'],
      ['google pay', 'g pay'],
      ['klarna'],
      ['mastercard'],
      ['paypal'],
      ['shop pay'],
      ['visa'],
    ];

    const selected = [];
    const used = new Set();

    preferredGroups.forEach(
      (keywords) => {
        const match =
          candidates.find(
            (candidate) => {
              if (
                used.has(candidate)
              ) {
                return false;
              }

              const label =
                this.getPaymentMethodLabel(
                  candidate
                );

              return keywords.some(
                (keyword) => {
                  return label.includes(
                    keyword
                  );
                }
              );
            }
          );

        if (match) {
          selected.push(match);
          used.add(match);
        }
      }
    );

    candidates.forEach(
      (candidate) => {
        if (
          selected.length >= 8 ||
          used.has(candidate)
        ) {
          return;
        }

        selected.push(candidate);
        used.add(candidate);
      }
    );

    if (
      selected.length === 0
    ) {
      this.addFallbackPaymentMethods(
        iconHolder
      );

      return;
    }

    const list =
      document.createElement('ul');

    list.className =
      'hlp-cart-payment-list';

    list.setAttribute(
      'role',
      'list'
    );

    selected
      .slice(0, 8)
      .forEach((method) => {
        const clone =
          method.cloneNode(true);

        clone.removeAttribute('id');

        clone
          .querySelectorAll('[id]')
          .forEach((element) => {
            element.removeAttribute(
              'id'
            );
          });

        list.appendChild(clone);
      });

    iconHolder.appendChild(list);
  }

  getPaymentMethodLabel(element) {
    const title =
      element.querySelector(
        'title'
      )?.textContent || '';

    const ariaLabel =
      element.getAttribute(
        'aria-label'
      ) || '';

    const text =
      element.textContent || '';

    return `${title} ${ariaLabel} ${text}`
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  addFallbackPaymentMethods(
    iconHolder
  ) {
    const fallbackMethods = [
      'AMEX',
      'Apple Pay',
      'G Pay',
      'Klarna',
      'Mastercard',
      'PayPal',
      'Shop Pay',
      'VISA',
    ];

    const list =
      document.createElement('div');

    list.className =
      'hlp-cart-payment-list hlp-cart-payment-list--fallback';

    fallbackMethods.forEach(
      (method) => {
        const badge =
          document.createElement(
            'span'
          );

        badge.textContent = method;

        list.appendChild(badge);
      }
    );

    iconHolder.appendChild(list);
  }

  async refreshCartData() {
    try {
      const root =
        window.Shopify?.routes?.root ||
        '/';

      const response =
        await fetch(
          `${root}cart.js`,
          {
            method: 'GET',
            credentials:
              'same-origin',
            headers: {
              Accept:
                'application/json',
            },
          }
        );

      if (!response.ok) {
        throw new Error(
          'Unable to load cart'
        );
      }

      const cart =
        await response.json();

      this.updateFromCart(cart);
    } catch (error) {
      console.error(
        '[HLP Cart Notification]',
        error
      );
    }
  }

  updateFromCart(cart) {
    this.currentCart = cart;

    const item =
      cart.items.find(
        (cartItem) => {
          return (
            cartItem.key ===
            this.cartItemKey
          );
        }
      ) ||
      cart.items[
        cart.items.length - 1
      ] ||
      null;

    this.currentItem = item;

    this.updateInlineSummary(
      cart,
      item
    );

    this.updateActionButtons(cart);
    this.updateCartBubble(
      cart.item_count
    );
  }

  updateInlineSummary(
    cart,
    item
  ) {
    const productPrice =
      this.notification.querySelector(
        '[data-hlp-product-price]'
      );

    const cartCount =
      this.notification.querySelector(
        '[data-hlp-cart-count]'
      );

    const cartTotal =
      this.notification.querySelector(
        '[data-hlp-cart-total]'
      );

    if (
      productPrice &&
      item
    ) {
      productPrice.textContent =
        this.formatMoney(
          item.final_price
        );
    }

    if (cartCount) {
      const itemWord =
        cart.item_count === 1
          ? this.strings.itemSingular
          : this.strings.itemPlural;

      cartCount.textContent =
        `${cart.item_count} ${itemWord} ${this.strings.inCart}`;
    }

    if (cartTotal) {
      cartTotal.textContent =
        this.formatMoney(
          cart.total_price
        );
    }
  }

  updateActionButtons(cart) {
    const buttonText =
      this.notification.querySelector(
        '[data-hlp-cart-button-text]'
      );

    if (!buttonText) return;

    buttonText.textContent =
      `${this.strings.viewCart} (${cart.item_count})`;
  }

  updateCartBubble(itemCount) {
    const cartIcon =
      document.getElementById(
        'cart-icon-bubble'
      );

    if (!cartIcon) return;

    let bubble =
      cartIcon.querySelector(
        '.cart-count-bubble'
      );

    if (itemCount <= 0) {
      bubble?.remove();
      return;
    }

    if (!bubble) {
      bubble =
        document.createElement(
          'div'
        );

      bubble.className =
        'cart-count-bubble';

      cartIcon.appendChild(bubble);
    }

    bubble.innerHTML = `
      <span aria-hidden="true">
        ${itemCount}
      </span>

      <span class="visually-hidden">
        ${itemCount} ${this.strings.itemCountLabel}
      </span>
    `;
  }

  formatMoney(cents) {
    const currency =
      this.currentCart?.currency ||
      window.Shopify?.currency
        ?.active ||
      'EUR';

    const locale =
      this.language === 'de'
        ? 'de-DE'
        : 'en-GB';

    try {
      return new Intl.NumberFormat(
        locale,
        {
          style: 'currency',
          currency,
        }
      ).format(
        (Number(cents) || 0) /
          100
      );
    } catch (error) {
      return `${(
        (Number(cents) || 0) /
        100
      ).toFixed(2)} €`;
    }
  }

  handleNotificationClick(event) {
    const closeButton =
      event.target.closest(
        '.cart-notification__close, .hlp-cart-continue'
      );

    if (!closeButton) return;

    event.preventDefault();
    this.close();
  }

  getSectionsToRender() {
    return [
      {
        id:
          'cart-notification-product',
        selector:
          `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id:
          'cart-notification-button',
      },
      {
        id:
          'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    const parsedDocument =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const element =
      parsedDocument.querySelector(
        selector
      );

    return element
      ? element.innerHTML
      : null;
  }

  handleBodyClick(event) {
    const target = event.target;

    if (
      target ===
        this.notification ||
      target.closest(
        'cart-notification'
      )
    ) {
      return;
    }

    const disclosure =
      target.closest(
        'details-disclosure, header-menu'
      );

    this.activeElement =
      disclosure
        ? disclosure.querySelector(
            'summary'
          )
        : null;

    this.close();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  async dispatchCartViewEvent() {
    const CartViewEvent =
      window.StandardEvents
        ?.CartViewEvent;

    if (!CartViewEvent) return;

    try {
      const root =
        window.Shopify?.routes?.root ||
        '/';

      const cartUrl =
        window.routes?.cart_url ||
        `${root}cart`;

      const response =
        await fetch(
          `${cartUrl}.json`
        );

      const cart =
        await response.json();

      if (!cart?.currency) {
        return;
      }

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart:
            CartViewEvent
              .createCartFromAjaxResponse(
                cart
              ),
        })
      );
    } catch (error) {
      /*
       * Analytics-only event:
       * cart functionality remains unaffected.
       */
    }
  }
}

if (
  !customElements.get(
    'cart-notification'
  )
) {
  customElements.define(
    'cart-notification',
    CartNotification
  );
}